import { appendCodexDebugLog } from "./codexDebugLog";

// Preparation and playback use separate clocks. Snapshots cannot spend an
// entrance before its first frame; completion follows the browser animation.
export class GameSurfaceMotion {
    private frame = 0;
    private generation = 0;
    private animations: Animation[] = [];
    private settle: (() => void) | null = null;
    private readonly reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    private readonly onReduced = () => { if (this.reduced.matches) this.finish(); };

    constructor(private readonly mode: string) {
        this.reduced.addEventListener("change", this.onReduced);
    }

    private queue(start: () => void) {
        const generation = this.generation;
        this.frame = requestAnimationFrame(() => {
            this.frame = requestAnimationFrame(() => {
                this.frame = 0;
                if (generation === this.generation) start();
            });
        });
    }

    prepare(start: () => void) {
        this.cancel();
        this.settle = start;
        this.queue(() => { this.settle = null; start(); });
    }

    play(target: HTMLElement | HTMLElement[], label: string, frames: Keyframe[], duration: number, done?: () => void) {
        this.cancel();
        const elements = (Array.isArray(target) ? target : [target]).filter(element => element.isConnected);
        if (this.reduced.matches || !elements.length || typeof elements[0]!.animate !== "function") { done?.(); return; }
        const requestedAt = performance.now();
        const generation = this.generation;
        this.settle = done ?? null;
        this.animations = elements.map(element => {
            const animation = element.animate(frames, { duration, easing: "cubic-bezier(0.22, 0.61, 0.36, 1)", fill: "both" });
            animation.id = `ziarcadia:${this.mode}:${label}`;
            animation.pause();
            animation.currentTime = 0;
            return animation;
        });
        this.queue(() => {
            if (this.reduced.matches || elements.some(element => !element.isConnected)) { this.finish(); return; }
            const startedAt = performance.now();
            this.animations.forEach(animation => animation.play());
            void Promise.all(this.animations.map(animation => animation.finished)).then(() => {
                if (generation !== this.generation) return;
                const prepareMs = Math.round(startedAt - requestedAt);
                const playbackMs = Math.round(performance.now() - startedAt);
                let debug = true;
                try { debug = localStorage.getItem("ziarcadia:surface-motion-debug") !== "0"; } catch { /* Optional diagnostics. */ }
                if (debug && (prepareMs >= 80 || playbackMs > duration + 120)) {
                    const line = `mode=${this.mode} surface=${label} prepare_ms=${prepareMs} playback_wall_ms=${playbackMs} expected_ms=${duration}`;
                    try {
                        console.info(`[Ziarcadia][SurfaceMotion] ${line}`);
                        appendCodexDebugLog(this.mode, "info", line);
                    } catch { /* Diagnostics must never hold the visual open. */ }
                }
                this.finish();
            }).catch(() => { /* New navigation or destroy cancelled playback. */ });
        });
    }

    private finish() {
        const done = this.settle;
        this.cancel();
        done?.();
    }

    cancel() {
        this.generation++;
        cancelAnimationFrame(this.frame);
        this.frame = 0;
        this.settle = null;
        this.animations.forEach(animation => animation.cancel());
        this.animations = [];
    }

    destroy() {
        this.cancel();
        this.reduced.removeEventListener("change", this.onReduced);
    }
}
